/* eslint-disable no-underscore-dangle */
import { TConsentManagement, TConsentManagementInstance } from './ConsentManagement';

declare const window: ExtendedWindow & {
    UC_UI: {
        showSecondLayer: () => void;
    };
    __ucCmp: {
        getConsentDetails: () => Promise<IConsentDetails>;
        refreshScripts: () => void;
    };
};

type TGenericPrefs = ReturnType<TConsentManagementInstance['prefs']>;
type TUCPrefs = TGenericPrefs & {
    settings: {
        usercentricsServiceMapping: Record<string, string>;
    };
};

/**
 * @param ConsentManagement Base widget for extending
 * @returns UsercentricsCM class
 */
function UsercentricsCMClassCreator(ConsentManagement: TConsentManagement) {
    class UsercentricsCM extends ConsentManagement {
        prefs() {
            const prefs = {
                ...super.prefs()
            } as TUCPrefs;

            prefs.settings.usercentricsServiceMapping ||= {};

            return prefs;
        }

        /**
         * @description Usercentrics specific initialization logic
         * This method initializes the Usercentrics consent provider
         * and sets up event listeners for consent changes.
         */
        initializeConsentProvider() {
            const serviceMapping = this.prefs().settings.usercentricsServiceMapping;

            const fallback = () => {
                // eslint-disable-next-line no-console
                console.error('Usercentrics consent provider is not available. Behave as if no consent had been given.');

                const consentState = Object.fromEntries(Object.values(serviceMapping).map(serviceName => [serviceName, false]));

                this.updateConsentState(consentState);
            };

            const init = () => {
                const ucCmp = window.__ucCmp;

                if (!ucCmp) {
                    fallback();

                    return;
                }

                ucCmp.getConsentDetails().then(consentDetails => {
                    this.saveConsentState(consentDetails);
                });

                ucCmp.refreshScripts();

                window.addEventListener('UC_CONSENT', event => {
                    const consentDetails = (event as CustomEvent<IConsentDetails>).detail;

                    this.saveConsentState(consentDetails);
                });
            };

            if (window.__ucCmp) {
                init();
            } else {
                window.addEventListener('UC_UI_INITIALIZED', init, { once: true });

                // fallback if not initialized within 500ms
                setTimeout(() => {
                    if (window.__ucCmp) {
                        init();
                    } else {
                        fallback();
                    }
                }, 500);
            }
        }

        /**
         * @description Transforms the UC consent to generic one and
         * saves the consent state based on the provided consent details.
         *
         * @param consentDetails - The details of the consent received from Usercentrics.
         */
        saveConsentState(consentDetails: IConsentDetails) {
            const serviceMapping = this.prefs().settings.usercentricsServiceMapping;
            const consentState = Object.fromEntries(
                Object.entries(consentDetails.services)
                    .filter(([serviceID]) => serviceMapping[serviceID])
                    .map(([serviceID, service]) => [serviceMapping[serviceID], !!service.consent?.given])
            );

            this.updateConsentState(consentState);
        }

        /**
         * @description Opens the Usercentrics settings page.
         * This method is called when the user clicks on the settings link.
         * And is triggered from the parent widget.
         */
        openSettings() {
            if (!window.UC_UI) {
                // eslint-disable-next-line no-console
                console.warn('Usercentrics consent provider is not available.');

                return;
            }

            window.UC_UI.showSecondLayer();
        }
    }

    return UsercentricsCM;
}

export type TUsercentricsCM = ReturnType<typeof UsercentricsCMClassCreator>;

export type TUsercentricsCMInstance = InstanceType<TUsercentricsCM>;

export default UsercentricsCMClassCreator;
